import { create } from '@bufbuild/protobuf';

import * as pbm from '@/grpc-gen/contact_pb';
import * as pbs from '@/grpc-gen/contact_manager_service_pb';

import ApiContact, { ContactMapping, ContactTitle } from '@/model/Contact';
import { strcmp } from '@/services/common';
import { dateFromDateString, dateFromDateTimeString, dateStringFromDate } from '@/services/time-utils';

// eslint-disable-next-line no-use-before-define
export type ContactSortFncType = (o1: Contact, o2: Contact) => number;

export enum ContactSortType {
  Name = 'name',
  CompanyName = 'companyName',
  Email = 'email',
  Phone = 'phone',
  LastVisit = 'lastVisit',
  VisitCount = 'visitCount',
}

export default class Contact extends ApiContact {
  lastVisit?: Date;

  visitCount?: number;

  isDeleted?: boolean;

  updated?: Date;

  static fromGrpcResponse(r: pbs.ContactListResponse, m?: ContactMapping): Contact {
    return Contact.fromGrpcModel(r.contact, m);
  }

  static fromGrpcModel(o?: pbm.Contact, m?: ContactMapping): Contact {
    const c = new Contact();

    c.id = Number(o?.id ?? 0);
    c.name = o?.name ?? '';
    c.phone = o?.phone ?? '';
    c.email = o?.email || undefined;
    c.address = o?.address || undefined;
    c.postalCode = o?.postalCode || undefined;
    c.city = o?.city || undefined;
    c.state = o?.state || undefined;
    c.companyName = o?.companyName || undefined;
    c.reservationCount = o?.reservationCount || undefined;
    c.isVIP = o?.isVIP || undefined;
    c.preferredLanguage = o?.preferredLanguage || undefined;
    c.dateOfBirth = dateFromDateString(o?.birthday) ?? undefined;
    c.newsletterSubscription = o?.newsletterSubscription || undefined;
    c.notes = o?.notes || undefined;
    c.title = (o?.title as any) as ContactTitle;
    c.lastVisit = dateFromDateTimeString(Number(o?.lastVisit ?? 0)) ?? undefined;
    c.visitCount = o?.visitCount || undefined;
    c.isDeleted = o?.isDeleted || undefined;

    c.country = m?.countryByCode.get(o?.country ?? '');

    c.updated = o?.dtUpdate ? new Date(Number(o.dtUpdate) * 1000) : undefined;

    return c;
  }

  toGrpcModel(): pbm.Contact {
    return create(pbm.ContactSchema, {
      id: BigInt(this.id),
      name: this.name ?? '',
      phone: this.phone ?? '',
      email: this.email ?? '',
      address: this.address ?? '',
      postalCode: this.postalCode ?? '',
      city: this.city ?? '',
      state: this.state ?? '',
      country: this.country?.code ?? '',
      companyName: this.companyName ?? '',
      reservationCount: this.reservationCount ?? 0,
      isVIP: this.isVIP ?? false,
      preferredLanguage: this.preferredLanguage ?? '',
      birthday: this.dateOfBirth ? parseInt(dateStringFromDate(this.dateOfBirth) ?? '0', 10) : 0,
      newsletterSubscription: this.newsletterSubscription ?? false,
      notes: this.notes ?? '',
      title: (this.title as any) ?? '',
      visitCount: this.visitCount ?? 0,
      lastVisit: 0n,
      isDeleted: this.isDeleted ?? false,
      dtCreate: 0n,
      dtUpdate: 0n,
    });
  }

  static compare(o1: Contact, o2: Contact): number {
    return Contact.compareName(o1, o2);
  }

  static compareName(o1: Contact, o2: Contact): number {
    const res = strcmp(o1.name ?? '', o2.name ?? '');
    if (res !== 0) return res;
    return o1.id - o2.id;
  }

  static compareCompany(o1: Contact, o2: Contact): number {
    const res = strcmp(o1.companyName ?? '', o2.companyName ?? '');
    if (res !== 0) return res;
    return o1.id - o2.id;
  }

  static compareEmail(o1: Contact, o2: Contact): number {
    const res = strcmp(o1.email ?? '', o2.email ?? '');
    if (res !== 0) return res;
    return o1.id - o2.id;
  }

  static comparePhone(o1: Contact, o2: Contact): number {
    const res = strcmp(o1.phone ?? '', o2.phone ?? '');
    if (res !== 0) return res;
    return o1.id - o2.id;
  }

  static compareLastVisit(o1: Contact, o2: Contact): number {
    const res = (o1.lastVisit ?? new Date(0)).getTime() - (o2.lastVisit ?? new Date(0)).getTime();
    if (res !== 0) return res;
    return o1.id - o2.id;
  }

  static compareVisitCount(o1: Contact, o2: Contact): number {
    const res = (o1.visitCount ?? 0) - (o2.visitCount ?? 0);
    if (res !== 0) return res;
    return o1.id - o2.id;
  }

  static compareFnc(type: ContactSortType): ContactSortFncType {
    switch (type) {
      case ContactSortType.Name: return Contact.compareName;
      case ContactSortType.CompanyName: return Contact.compareCompany;
      case ContactSortType.Email: return Contact.compareEmail;
      case ContactSortType.Phone: return Contact.comparePhone;
      case ContactSortType.LastVisit: return Contact.compareLastVisit;
      case ContactSortType.VisitCount: return Contact.compareVisitCount;
      default: return Contact.compareName;
    }
  }

  static compareFncDesc(type: ContactSortType): ContactSortFncType {
    const fnc = Contact.compareFnc(type);
    return (o1: Contact, o2: Contact) => -fnc(o1, o2);
  }

  normalizedEmail(): string {
    if (!this.email) return '';
    return this.email.toLowerCase();
  }

  normalizedPhone(dialCode: string): string {
    if (!this.phone || !dialCode) return '';
    let phone = this.phone.replace(/[^\d+]/g, '');
    if (phone.length < 6 || phone.startsWith('+') || phone.startsWith('00')) return phone;
    if (phone.startsWith('0')) phone = phone.slice(2);
    return dialCode + phone;
  }
}
